import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, Alert } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import TablesSection from "../../components/PagesComponents/PosSection/PosPageTaps/TablesSectionTap/TablesSectionDesktop";
import DeliverySection from "../../components/PagesComponents/PosSection/PosPageTaps/DeliverySectionTap/DeliverySection";
import TakeawaySection from "../../components/PagesComponents/PosSection/PosPageTaps/TakeawaySectionTap/TakeawaySection";
import ReservationSection from "../../components/PagesComponents/PosSection/PosPageTaps/ReservationSectionTap/ReservationSection";
// new: استيراد hook الخاص بالميزات
import { useFeatures } from "../../context/PermissionsAndFeatures/FeaturesContext";

const POS = ({ isSidebarOpen }) => {
  // call features hook unconditionally
  const features = useFeatures();

  // helper to resolve feature flag safely
  const resolveFeature = (featObj, key) => {
    if (featObj == null) return null;
    try {
      if (typeof featObj === "function") return featObj(key);
      if (typeof featObj.isEnabled === "function") return featObj.isEnabled(key);
      if (typeof featObj.hasFeature === "function") return featObj.hasFeature(key);
      if (typeof featObj.get === "function") return featObj.get(key);
      if (key in featObj) return featObj[key];
    } catch (e) {
      return null;
    }
    return null;
  };

  const isTakeawayEnabled = resolveFeature(features, "operation_takeaway") !== false;
  // new: check delivery feature
  const isDeliveryEnabled = resolveFeature(features, "operation_delivery") !== false;
  // new: check table feature
  const isTableEnabled = resolveFeature(features, "operation_table") !== false;

  // initialize tabIndex from localStorage (unchanged behavior)
  const [tabIndex, setTabIndex] = useState(() => {
    const savedTabIndex = localStorage.getItem("posTabIndex");
    return savedTabIndex ? parseInt(savedTabIndex, 10) : 0;
  });

  useEffect(() => {
    localStorage.setItem("posTabIndex", tabIndex);
  }, [tabIndex]);

  // build tabs list: TAKEAWAY always visible now
  const tabs = [
    { id: "tables", label: "الطاولات", url: "/tables" },
    { id: "delivery", label: "الدليفري", url: "/delivery" },
    { id: "takeaway", label: "التيك أواي", url: "/takeaway" }, // visible always
    { id: "reservation", label: "الحجز", url: "/reservation" },
  ];

  // if saved index is out of bounds (e.g. feature toggled off), clamp it
  useEffect(() => {
    if (tabIndex >= tabs.length) {
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  const handleChange = (index) => {
    setTabIndex(index);
  };

  const openInNewTab = () => {
    const tab = tabs[tabIndex];
    if (!tab) return;

    // if opening tables and feature disabled -> show friendly message in new tab
    if (tab.id === "tables" && !isTableEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>الطاولات - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة الطاولات غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الطاولات.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // if opening takeaway and feature disabled -> show friendly message in new tab
    if (tab.id === "takeaway" && !isTakeawayEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>التيك أواي - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة التيك أواي غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة التيك أواي.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // if opening delivery and feature disabled -> show friendly message in new tab
    if (tab.id === "delivery" && !isDeliveryEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>الدليفري - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة الدليفري غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الدليفري.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // normal behavior
    window.open(tab.url, "_blank");
  };

  // render content based on active tab id
  const renderTabContent = (tabId) => {
    switch (tabId) {
      case "tables":
        if (!isTableEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة الطاولات غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <TablesSection />;
      case "delivery":
        if (!isDeliveryEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة الدليفري غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <DeliverySection />;
      case "takeaway":
        // if feature disabled show message instead of data
        if (!isTakeawayEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة التيك أواي غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <TakeawaySection />;
      case "reservation":
        return <ReservationSection />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%", p: 0 }}>
      {/* ✅ شريط التبويبات غير ثابت أثناء التمرير */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "18px",
          backgroundColor: "transparent",
          padding: "10px 0",
          marginTop: "1px", // ✅ تقليل المسافة من الأعلى
          marginBottom: "0px", // ✅ تقليل المسافة بين الأزرار والطاولات
          transition: "all 0.3s ease-in-out",
        }}
      >
        {tabs.map((tab, index) => (
          <Button
            key={tab.id}
            onClick={() => handleChange(index)}
            sx={{
              backgroundColor: tabIndex === index ? "#814C3B" : "#EA7C69",
              color: "#fff",
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "6px",
              padding: "10px 20px",
              minWidth: "150px",
              transition: "all 0.3s ease-in-out",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: tabIndex === index ? "#6E3A2F" : "#D97B5A",
              },
            }}
          >
            {tab.label}
          </Button>
        ))}

        {/* ✅ زر فتح القسم في نافذة جديدة */}
        <IconButton
          color="primary"
          onClick={openInNewTab}
          title="فتح في تبويب جديد"
          sx={{
            backgroundColor: "#EA7C69",
            color: "#fff",
            borderRadius: "6px",
            padding: "10px",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              backgroundColor: "#D97B5A",
            },
          }}
        >
          <OpenInNew />
        </IconButton>
      </Box>

      {/* ✅ محتوى الصفحة مع تقليل المسافة بين الأزرار والطاولات */}
      <Box sx={{ mt: "0px", p: 3 }}> {/* ✅ تقليل المسافة هنا */}
        {/* render the active tab content based on tabs array */}
        {renderTabContent(tabs[tabIndex]?.id)}
      </Box>
    </Box>
  );
};

export default POS;
